import React, { useState, FC, useEffect, useContext, useMemo } from 'react';
import TurbineActions from './TurbineActions';
import styles from '../Annotation.module.scss';
import { AppDispatch } from '../../../../store';
import { RootState } from '../../../../rootReducer';
import { connect } from 'react-redux';
import { prepareSequence, prepareNodeData, setPathContext, setCurrentPath, setStatusTable, setLastCalledPath } from '../annotation.slice';
import { PathContextType } from '../annotation.types';
import { ArrowIconLeft } from '../ArrowIconLeft';
import { INITIAL_TREE_WIDTH, MIN_TREE_WIDTH, MAX_TREE_WIDTH, isSequenceValidForAnnotation, AnnotationManagerPrefix } from '../Helpers';
import { Resizer } from '../Resizer';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { MatchFindingPathParams } from '../../../../shared/models/matchTurbinePathParams';
import TurbineStatusMenu from './TurbineStatus/TurbineStatusMenu';
import TreeView from './TreeView';
import { prepareTurbineTreeStructure } from './utils';
import { ParserContext } from '../../contexts/ParserContext';
import { isStringNullOrEmpty } from '../AnnotationForm/Utils';
import { TABLE_STATUS } from '../../../../shared/Helpers';

type Props = {
  hideTree: boolean;
  hideStichedView: boolean;
  setHideTree: (hide: boolean) => void;
  setTreeWidth: (treeWidth: number) => void;
};

type StoreProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const TreeContainer: FC<RouteComponentProps<MatchFindingPathParams> & Props & StoreProps> = ({
  history,
  match,
  hideTree,
  loaded,
  fullScreenMode,
  sequenceStatuses,
  isLoadingImages,
  image,
  pathContext,
  currentPath,
  onPrepareSequence,
  setHideTree,
  setTreeWidth,
  onPrepareNode,
  onPathContext,
  onCurrentPath,
  onTableStatus,
  onLastCalledPath
}) => {
  const [treeHovered, setTreeHovered] = useState(false);
  const [width, setWidth] = React.useState(INITIAL_TREE_WIDTH);

  const { parser } = useContext(ParserContext);

  const rootPath = useMemo(() => parser.parsedRequiredPath(match.params), [match.params, parser]);
  const rootNode = useMemo(() => parser.getRootNode(match.params), [match.params, parser]);
  const treeStructure = useMemo(
    () => prepareTurbineTreeStructure(rootNode, parser.parsedRequiredPath(match.params), sequenceStatuses),
    [match.params, parser, rootNode, sequenceStatuses]
  );

  const onNodeSelected = (nodeKey: string) => {
    const isLeaf = parser.isPathSequence(nodeKey);

    let canOpen = true;

    if (isLeaf) {
      canOpen = isSequenceValidForAnnotation(nodeKey, sequenceStatuses);
      onPathContext('with-images');
    } else {
      onPathContext('no-images');
    }

    if (canOpen) {
      history.push(`/${AnnotationManagerPrefix}/${parser.name}/${nodeKey}`);
    }
  };

  useEffect(() => {
    const path = parser.parsedFullPath(match.params);

    const untilImagePath = parser.getPathUntilImage(match.params);
    const image = parser.getImageName(match.params);

    if (isStringNullOrEmpty(currentPath) && parser.getAnnotationId(match.params)) {
      onPathContext('with-images');
    }

    if (currentPath !== path) {
      onCurrentPath(path);

      if (pathContext === 'with-images') {
        onLastCalledPath('');
      }
    }

    if (isStringNullOrEmpty(currentPath)) {
      if (!isStringNullOrEmpty(parser.getAnnotationId(match.params)) || !isStringNullOrEmpty(parser.getImageName(match.params))) {
        onPathContext('with-images');
      }
      return;
    }

    if (pathContext === 'no-images' && parser.hasSequencePathChanged(match.params, currentPath) && !parser.getAnnotationId(match.params)) {
      onPrepareNode(untilImagePath, image);
      onTableStatus(TABLE_STATUS.EXPANDED);
    }

    if (parser.containsSequence(path)) {
      onPrepareSequence(parser.parsedSequencePath(match.params));
    }
  }, [
    currentPath,
    match.params,
    onCurrentPath,
    onLastCalledPath,
    onPathContext,
    onPrepareNode,
    onPrepareSequence,
    onTableStatus,
    parser,
    pathContext
  ]);

  return (
    <Resizer
      setWidth={setWidth}
      fullScreenMode={fullScreenMode}
      widthProp={width}
      left={false}
      minWidth={MIN_TREE_WIDTH}
      maxWidth={MAX_TREE_WIDTH}
      enable={loaded && !!image}
      setHideTree={setHideTree}
      setTreeWidth={setTreeWidth}
      hideTree={hideTree}
    >
      <div style={{ height: '100%', position: 'relative' }} onMouseEnter={() => setTreeHovered(true)} onMouseLeave={() => setTreeHovered(false)}>
        <TurbineActions turbinePath={rootPath} />
        <TurbineStatusMenu />
        <div className={`${styles.treeContainer} ${styles.noselect}`}>
          <TreeView
            disabled={isLoadingImages || (sequenceStatuses && !sequenceStatuses.length)}
            selectedKey={currentPath}
            treeStructure={treeStructure}
            onNodeSelected={onNodeSelected}
          />
        </div>
        <ArrowIconLeft
          isVisible={treeHovered}
          isImageLoaded={loaded && !!image}
          right='-14.5px'
          top='5.7%'
          fullscreen={fullScreenMode}
          OnChangeTreeAndViewImage={setHideTree}
          treeOrViewImage={hideTree}
          treeIcon={true}
          hideIconTree={hideTree}
        />
      </div>
    </Resizer>
  );
};

const mapStateToProps = (state: RootState) => ({
  sequenceStatuses: state.annotation.sequenceStatuses,
  isLoadingImages: state.annotation.isLoadingImages,
  fullScreenMode: state.annotation.fullScreenMode,
  loaded: state.canvas.loaded,
  image: state.annotation.image,
  pathContext: state.annotation.pathContext,
  currentPath: state.annotation.currentPath
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onPrepareSequence: (sequencePath: string) => dispatch(prepareSequence(sequencePath)),
  onPrepareNode: (nodePath: string, imageName?: string) => dispatch(prepareNodeData(nodePath, imageName)),
  onPathContext: (context: PathContextType) => dispatch(setPathContext(context)),
  onCurrentPath: (path: string) => dispatch(setCurrentPath(path)),
  onTableStatus: (value: number) => dispatch(setStatusTable(value)),
  onLastCalledPath: (path: string) => dispatch(setLastCalledPath(path))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TreeContainer));
